import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react'
import { ANALOG_SCALE_LEFT } from '../constants/visualizer'
import {
  deliveredStereoChannels,
  stereoInputConstraints,
} from '../audio/stereoInput'
import { SharedInputContext } from '../audio/sharedInput'

/**
 * @param {{ isListening: boolean, movementCoef?: number, deviceId: string, backgroundImage?: string | null, scaleCoefficient?: number, channel?: 'left' | 'right' }} props
 */
const VUMeter = ({
  isListening,
  movementCoef = ANALOG_SCALE_LEFT,
  deviceId,
  backgroundImage = null,
  scaleCoefficient = 1.0,
  channel = null,
}) => {
  const sharedInput = useContext(SharedInputContext)
  const needleRef = useRef(null)
  const audioContextRef = useRef(null)
  const analyserRef = useRef(null)
  const animationFrameRef = useRef(null)
  const streamRef = useRef(null)
  const lastRotationRef = useRef(-50)
  const scaleCoefficientRef = useRef(scaleCoefficient)

  useEffect(() => {
    scaleCoefficientRef.current = scaleCoefficient
  }, [scaleCoefficient])

  const paintNeedle = (rotation) => {
    needleRef.current?.setAttribute(
      'transform',
      `rotate(${rotation}, 100, 95)`
    )
  }

  useLayoutEffect(() => {
    paintNeedle(lastRotationRef.current)
  })

  useLayoutEffect(() => {
    let cancelled = false
    let retained = null
    let splitter = null
    let feed = null
    let tap = null

    const resetNeedle = () => {
      lastRotationRef.current = -50
      paintNeedle(-50)
    }

    let unsubscribeFrame = null
    let sharedSplitter = false

    const stopOwnedAudio = () => {
      unsubscribeFrame?.()
      unsubscribeFrame = null
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      if (feed && tap) {
        try {
          feed.disconnect(tap)
        } catch {
          // The context was already closed.
        }
      }
      if (sharedSplitter && splitter && analyserRef.current) {
        try {
          splitter.disconnect(analyserRef.current)
        } catch {
          // Already disconnected.
        }
      } else {
        splitter?.disconnect()
      }
      analyserRef.current?.disconnect()
      analyserRef.current = null
      streamRef.current?.getTracks().forEach((track) => track.stop())
      streamRef.current = null
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== 'closed'
      ) {
        audioContextRef.current.close().catch(() => {})
      }
      audioContextRef.current = null
      retained?.release()
      retained = null
    }

    const analyze = () => {
      const analyser = analyserRef.current
      if (!analyser) return
      if (!analyze.data) {
        analyze.data = new Uint8Array(analyser.frequencyBinCount)
      }
      const dataArray = analyze.data
      analyser.getByteFrequencyData(dataArray)

      let sum = 0
      for (let i = 0; i < dataArray.length; i++) sum += dataArray[i]
      const average = sum / dataArray.length
      const fast = Boolean(retained?.fastRef?.current)

      // Cap scale coefficient at knob value 20 (which is ~3.16x scale)
      const maxScale = Math.pow(5, 20 / 50)
      const boost =
        retained?.scaleRef?.current ?? scaleCoefficientRef.current
      const effectiveScale = Math.min(boost, maxScale)
      const targetRotation =
        -50 + (average / 255) * 100 * movementCoef * effectiveScale
      const clampedTarget = Math.max(-50, Math.min(50, targetRotation))

      // Needles stay ballistic. FAST only shortens the sweep; the canvas
      // constant (0.25) makes a meter twitch like a peak LED.
      analyser.smoothingTimeConstant = fast ? 0.65 : 0.8
      const smoothing = fast ? 0.28 : 0.15
      const newRotation =
        lastRotationRef.current +
        (clampedTarget - lastRotationRef.current) * smoothing

      lastRotationRef.current = newRotation
      paintNeedle(newRotation)
    }

    const loop = () => {
      analyze()
      animationFrameRef.current = requestAnimationFrame(loop)
    }

    if (!isListening) {
      stopOwnedAudio()
      resetNeedle()
      return
    }

    if (sharedInput === null) {
      resetNeedle()
      return
    }

    if (sharedInput) {
      if (sharedInput.context.state === 'closed') return
      sharedInput.retain()
      retained = sharedInput
      const analyser = sharedInput.context.createAnalyser()
      analyser.fftSize = 256
      analyser.smoothingTimeConstant = 0.8
      if (channel && sharedInput.deliveredChannels >= 2) {
        if (sharedInput.splitter) {
          splitter = sharedInput.splitter
          sharedSplitter = true
          splitter.connect(analyser, channel === 'right' ? 1 : 0)
          tap = analyser
          feed = splitter
        } else {
          splitter = sharedInput.context.createChannelSplitter(2)
          splitter.connect(analyser, channel === 'right' ? 1 : 0)
          tap = splitter
          feed = sharedInput.source
          feed.connect(tap)
        }
      } else {
        tap = analyser
        feed = sharedInput.source
        feed.connect(tap)
      }
      analyserRef.current = analyser
      unsubscribeFrame = sharedInput.subscribeFrame(analyze)
      return () => {
        cancelled = true
        stopOwnedAudio()
      }
    }

    const initAudio = async () => {
      try {
        if (
          audioContextRef.current &&
          audioContextRef.current.state !== 'closed'
        ) {
          await audioContextRef.current.close().catch(() => {})
        }
        if (cancelled) return

        const audioContext = new (window.AudioContext ||
          window.webkitAudioContext)()
        audioContextRef.current = audioContext

        if (audioContext.state === 'suspended') {
          await audioContext.resume()
        }
        if (cancelled) return

        const analyser = audioContext.createAnalyser()
        analyser.fftSize = 256

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: stereoInputConstraints(deviceId),
        })
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        streamRef.current?.getTracks().forEach((track) => track.stop())
        streamRef.current = stream
        const source = audioContext.createMediaStreamSource(stream)
        const deliveredChannels = await deliveredStereoChannels(
          stream.getAudioTracks()[0]
        )
        if (cancelled) return
        if (channel && deliveredChannels >= 2) {
          splitter = audioContext.createChannelSplitter(2)
          source.connect(splitter)
          splitter.connect(analyser, channel === 'right' ? 1 : 0)
        } else {
          source.connect(analyser)
        }

        analyserRef.current = analyser
        setTimeout(() => {
          if (!cancelled) loop()
        }, 100)
      } catch (error) {
        console.error('Error accessing microphone:', error)
      }
    }

    initAudio()

    return () => {
      cancelled = true
      stopOwnedAudio()
    }
  }, [isListening, deviceId, channel, movementCoef, sharedInput])

  return (
    <div
      className="vu-meter"
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
        backgroundSize: '100% 100%',
        width: '100%',
        height: '100%',
      }}
    >
      <div className="meter-scale">
        <svg
          viewBox="0 0 200 100"
          className="meter-svg"
          preserveAspectRatio="none"
        >
          <g ref={needleRef} transform="rotate(-50, 100, 95)">
            <line
              x1="100"
              y1="95"
              x2="112"
              y2="12"
              stroke="#CCCCCC"
              strokeWidth="1"
            />
          </g>
        </svg>
      </div>
    </div>
  )
}

export default VUMeter
